// Implements credential and password workflows using Supabase browser auth and function entrypoints.
import { supabase } from '@/integrations/supabase/client';
import type { AuthCredentialsPort } from '../core/types';
import { buildAuthCallbackUrl, extractFunctionError } from './authFunctionSupport';
import { env } from '@/lib/env';

function shouldCaptureVerificationLink(): boolean {
  return (env.VITE_E2E_CAPTURE_VERIFICATION_LINK || '').trim().toLowerCase() === 'true';
}

export function createSupabaseAuthCredentialsPort(): AuthCredentialsPort {
  return {
    async signUp(input) {
      const { data, error, response } = await supabase.functions.invoke('sign-up-with-email', {
        body: {
          email: input.email,
          password: input.password,
          fullName: input.fullName,
          redirectTo: buildAuthCallbackUrl(),
        },
        headers: shouldCaptureVerificationLink()
          ? {
              'x-sportbook-e2e-capture-verification-link': '1',
            }
          : undefined,
      });

      if (error) {
        return {
          error: await extractFunctionError(error, response),
          emailConfirmationRequired: true,
        };
      }

      if (data?.error) {
        return { error: new Error(data.error), emailConfirmationRequired: true };
      }

      return {
        error: null,
        userId: data?.userId,
        emailConfirmationRequired: data?.emailConfirmationRequired ?? true,
      };
    },

    async signIn(input) {
      const { error } = await supabase.auth.signInWithPassword({
        email: input.email,
        password: input.password,
      });
      return { error };
    },

    async requestPasswordReset(input) {
      const { data, error } = await supabase.functions.invoke('request-password-reset', {
        body: {
          email: input.email,
          redirectTo: buildAuthCallbackUrl(),
        },
      });

      if (error) {
        return { error };
      }

      if (data?.error) {
        return { error: new Error(data.error) };
      }

      return { error: null };
    },

    async updatePassword(input) {
      const { error } = await supabase.auth.updateUser({
        password: input.password,
      });
      return { error };
    },
  };
}
